import dayjs from "dayjs";
import axiosInstance from "../../services/axiosInstance";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { capitalizeFirstLetter } from "../../utils/strings";
import { useModalStore } from "../../store/modalStore";
import { useAuthStore } from "../../store/authStore";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Tooltip,
  Typography,
  Card,
  Skeleton,
  Chip,
} from "@mui/material";

import {
  PersonAddAlt,
  RestartAlt,
  TransferWithinAStation,
  ToggleOff,
  ToggleOn,
  AdminPanelSettings,
  Edit,
} from "@mui/icons-material";

// Interface for user data
interface User {
  id: number;
  email: string;
  isActive: boolean;
  lastOnline: string;
  role: string;
}

const UserManagementPage: React.FC = () => {
  // States
  const setRoleModalOpen = useModalStore((state) => state.setRoleModalOpen);
  const setRoleEmail = useModalStore((state) => state.setRoleEmail);
  const setRole = useModalStore((state) => state.setRole);
  const setPasswordResetModalOpen = useModalStore(
    (state) => state.setPasswordResetModalOpen
  );

  const [users, setUsers] = useState<User[]>([]);
  const [error, setError] = useState(null);
  const [openCreateDialog, setOpenCreateDialog] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [newUser, setNewUser] = useState<{
    email: string;
    password: string;
  }>({
    email: "",
    password: "",
  });
  const { userDetails } = useAuthStore();

  // API calls
  const getUsers = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("/api/v1/users/all-users");
      if (response.status === 200) {
        setUsers(response.data.data);
      }
    } catch (error: any) {
      toast.error("Error fetching users. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getUsers();
  }, []);

  // Handlers
  // Toggle user isActive status (disable or enable)
  const toggleUserStatus = async (id: number, userStatus: boolean) => {
    try {
      let url = "";
      if (userStatus) {
        url = "disable";
      } else {
        url = "enable";
      }
      await axiosInstance.post(`/api/v1/users/${id}/${url}-user`);
      setUsers((prev) =>
        prev.map((user) =>
          user.id === id ? { ...user, isActive: !user.isActive } : user
        )
      );
    } catch (error: any) {
      setError(error?.message);
      toast.error("Error toggling user status. Please try again.");
    }
  };

  // Reset user password handler
  const resetUserPassword = async (email: string) => {
    setPasswordResetModalOpen(true);
    setRoleEmail(email);
  };

  // Change user role handler
  const changeUserRole = async (email: string, role: string) => {
    setRoleModalOpen(true);
    setRoleEmail(email);
    setRole(role);
  };

  // Handle create user form submission
  const handleCreateUser = async () => {
    const newUserData = { ...newUser };

    try {
      const response = await axiosInstance.post(
        `/api/v1/users/admin-signup`,
        newUserData
      );
      setUsers((prev) => [
        ...prev,
        {
          email: response.data.data.email,
          role: response.data.data.role,
          isActive: response.data.data.isActive,
          lastOnline: response.data.data.lastOnline,
          id: response.data.data.id,
        },
      ]);
      setNewUser({ email: "", password: "" });
      setOpenCreateDialog(false);
    } catch (error: any) {
      setError(error?.message);
      toast.error("Error creating user. Please try again.");
    }
  };

  // Helper to create skeletons for rows
  const renderSkeletonRows = () => {
    const skeletons = [];
    for (let i = 0; i < 3; i++) {
      // Display 5 skeleton rows as a placeholder
      skeletons.push(
        <TableRow key={i}>
          <TableCell>
            <Skeleton variant="text" width="100%" height={20} />
          </TableCell>
          <TableCell>
            <Skeleton variant="text" width="60%" height={20} />
          </TableCell>
          <TableCell>
            <Skeleton variant="text" width="60%" height={20} />
          </TableCell>
          <TableCell>
            <Skeleton variant="circular" width={24} height={24} />
          </TableCell>
        </TableRow>
      );
    }
    return skeletons;
  };

  return (
    <Box mt={4} mx={4} height={"85vh"}>
      {/* Skeleton Loading Card for Users Listing */}
      <Card
        sx={{
          p: 1,
          margin: "auto",
          width: "100%",
          backgroundColor: "#343434",
          color: "white",
          textAlign: "left",
        }}
      >
        <motion.div
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.5, type: "spring", stiffness: 120 }}
          className="text-xl font-medium"
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: "bold",
              fontSize: "2rem",
              textShadow: "1px 1px 2px rgba(0, 0, 0, 0.15)",
            }}
          >
            Users Listing
          </Typography>
        </motion.div>
      </Card>

      {/* Users Table */}
      <TableContainer
        component={Paper}
        sx={{ marginTop: 3 }}
        variant="elevation"
      >
        {loading ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Email</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Role</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {renderSkeletonRows()} {/* Render skeleton rows here */}
            </TableBody>
          </Table>
        ) : (
          <motion.text
            initial={{ filter: "blur(20px)", opacity: 0 }}
            animate={{ filter: "blur(0px)", opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <Table size="small" border={1}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#B0B0B0" }}>
                  <TableCell sx={{ fontWeight: "bold", fontSize: 16 }}>
                    Email
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: 16 }}>
                    Role
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: 16 }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: 16 }}>
                    Actions
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: 16 }}>
                    Last logged in
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow
                    key={user.id}
                    sx={{
                      backgroundColor: user.isActive ? "white" : "#F5F5F5", // Disabled row background color
                      opacity: user.isActive ? 1 : 0.6, // Reduced opacity for disabled row
                      // pointerEvents: user.isActive ? "auto" : "none", // Disable pointer events (e.g., clicks)
                    }}
                  >
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={capitalizeFirstLetter(user.role)}
                        icon={
                          user.role === "admin" ? (
                            <AdminPanelSettings />
                          ) : (
                            <Edit />
                          )
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {user.isActive ? "Active" : "Disabled"}
                    </TableCell>
                    <TableCell>
                      <Box
                        display="flex"
                        gap={1}
                        justifyContent="center"
                        alignItems="center"
                      >
                        <Tooltip title="Reset Password" arrow>
                          <span>
                            <IconButton
                              onClick={() => resetUserPassword(user.email)}
                              sx={{
                                border: "1px solid #B0B0B0",
                                borderRadius: "4px",
                                padding: "6px",
                                "&:hover": {
                                  backgroundColor: "#E0E0E0",
                                  borderColor: "#A0A0A0",
                                },
                              }}
                              disabled={!user.isActive} // Disable button if user is not active
                            >
                              <RestartAlt color="info" />
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip title="Change Role" arrow>
                          <span>
                            <IconButton
                              onClick={() =>
                                changeUserRole(user.email, user.role)
                              }
                              sx={{
                                border: "1px solid #B0B0B0",
                                borderRadius: "4px",
                                padding: "6px",
                                "&:hover": {
                                  backgroundColor: "#E0E0E0",
                                  borderColor: "#A0A0A0",
                                },
                              }}
                              disabled={
                                !user.isActive ||
                                userDetails.email === user.email
                              }
                            >
                              <TransferWithinAStation color="secondary" />
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip
                          title={
                            user.isActive ? "Disable User" : "Activate User"
                          }
                          arrow
                        >
                          <span>
                            <IconButton
                              onClick={() =>
                                toggleUserStatus(user.id, user.isActive)
                              }
                              sx={{
                                border: "1px solid #B0B0B0",
                                borderRadius: "4px",
                                padding: "6px",
                                "&:hover": {
                                  backgroundColor: user.isActive
                                    ? "#FFCDD2"
                                    : "#C8E6C9",
                                },
                              }}
                              // disabled={!user.isActive}
                            >
                              {user.isActive ? (
                                <ToggleOn
                                  color={user.isActive ? "success" : "error"}
                                />
                              ) : (
                                <ToggleOff
                                  color={user.isActive ? "success" : "error"}
                                />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {user.lastOnline
                        ? dayjs(user.lastOnline).format("hh:mm A MM-DD-YYYY")
                        : "Never"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </motion.text>
        )}
      </TableContainer>

      {/* Button to open Create User Dialog */}
      <Box textAlign={"center"} mt={2}>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => setOpenCreateDialog(true)}
          startIcon={<PersonAddAlt />}
          fullWidth
        >
          Create User
        </Button>
      </Box>

      {/* Create User Dialog */}
      <Dialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
      >
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Email"
            fullWidth
            value={newUser.email}
            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
            variant="outlined"
          />
          <TextField
            margin="dense"
            label="Password"
            type="password"
            fullWidth
            value={newUser.password}
            onChange={(e) =>
              setNewUser({ ...newUser, password: e.target.value })
            }
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleCreateUser} color="primary">
            Create
          </Button>
        </DialogActions>
        {error && <Typography color="error">{error}</Typography>}
      </Dialog>
    </Box>
  );
};

export default UserManagementPage;
